// Client profile → Payments tab: what's been paid, what's owed, the payment
// plan and its status, and the full payment history (online, recorded by
// staff, or imported). Payments are voided with a reason, never deleted.
import { useCallback, useEffect, useState } from "react";
import { Modal } from "../Modal";
import RecordPaymentModal from "./RecordPaymentModal";
import TakePaymentModal from "./TakePaymentModal";
import {
  PAYMENT_METHOD_LABEL, PLAN_STATUS_LABEL, payments as paymentsApi,
  type ClientPayment, type PaymentsOverview, type PlanStatus, type PlanType,
} from "../../lib/api";

const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
// A calendar date (YYYY-MM-DD) is shown as that exact day — parsed at local
// noon so no time zone can move it to the day before.
const calendarDay = (ymd: string) => {
  const d = new Date(`${ymd.slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const day = (iso: string | null) => {
  if (!iso) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return calendarDay(iso);
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
/** Date a payment was received: the server's calendar date when given; for
 *  staff-entered dates (stored at UTC midnight) the date part itself. */
const paidDay = (p: ClientPayment) =>
  p.paid_on ? calendarDay(p.paid_on) : p.source === "online" ? day(p.paid_at) : calendarDay(p.paid_at);
const NOT_RECEIVED: Record<string, string> = { failed: "Failed — not received", pending: "Pending — not confirmed", refunded: "Refunded" };
const SOURCE_LABEL: Record<string, string> = { online: "Online", staff: "Recorded by staff", imported: "Imported" };
const PLAN_OPTIONS: Array<{ value: PlanType | ""; label: string }> = [
  { value: "", label: "No plan" },
  { value: "monthly", label: "Monthly ($99/mo)" },
  { value: "six_month", label: "6 months for $450" },
  { value: "paid_in_full", label: "Paid in full" },
  { value: "free", label: "Free" },
  { value: "custom", label: "Custom" },
];

export function PlanStatusBadge({ status }: { status: PlanStatus | null | undefined }) {
  if (!status || status === "no_plan") return null;
  const tone = status === "past_due"
    ? { background: "var(--ne-red-soft)", color: "var(--ne-red-dark)" }
    : status === "paid_in_full" || status === "current"
      ? { background: "var(--ne-success-bg)", color: "var(--ne-success-text)", border: "1px solid var(--ne-success-border)" }
      : {};
  return <span className="status-pill" style={tone} data-testid="plan-status-badge">{PLAN_STATUS_LABEL[status]}</span>;
}

function appliedLabel(p: ClientPayment) {
  if (p.applied_to === "invoice") return p.invoice_label || (p.invoice_id ? `Invoice ${p.invoice_id.slice(0, 8)}` : "Invoice");
  if (p.applied_to === "round") return p.round_number != null ? `Round ${p.round_number}` : "Dispute round";
  return "Prior balance / pre-Hub";
}

export default function PaymentsTab({ clientId, canEdit = true, onPlanStatus }: {
  clientId: string;
  canEdit?: boolean;
  onPlanStatus?: (s: PlanStatus) => void;
}) {
  const [data, setData] = useState<PaymentsOverview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [recordOpen, setRecordOpen] = useState(false);
  const [takeOpen, setTakeOpen] = useState(false);
  const [voiding, setVoiding] = useState<ClientPayment | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const [voidBusy, setVoidBusy] = useState(false);
  const [history, setHistory] = useState<{ payment: ClientPayment; rows: Array<{ action: string; actor_name?: string | null; at: string; reason?: string | null }> } | null>(null);
  const [planOpen, setPlanOpen] = useState(false);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const d = await paymentsApi.overview(clientId);
      setData(d); onPlanStatus?.(d.summary.plan_status);
    } catch (e: any) { setErr(e?.body?.message || e?.message || "Couldn't load payments."); }
  }, [clientId, onPlanStatus]);
  useEffect(() => { setData(null); load(); }, [load]);

  async function doVoid() {
    if (!voiding) return;
    if (voidReason.trim().length < 3) return;
    setVoidBusy(true);
    try {
      await paymentsApi.void(voiding.id, voidReason.trim());
      setVoiding(null); setVoidReason(""); setNotice("Payment voided. It stays in the history with your reason."); await load();
    } catch (e: any) { setErr(e?.body?.message || e?.message || "Couldn't void the payment."); }
    finally { setVoidBusy(false); }
  }

  async function showHistory(p: ClientPayment) {
    try { const r = await paymentsApi.audit(p.id); setHistory({ payment: p, rows: r.audit }); }
    catch (e: any) { setErr(e?.body?.message || e?.message || "Couldn't load the change history."); }
  }

  if (err && !data) return <div className="sub-panel"><p className="error-text" role="alert">{err}</p><button className="btn-secondary" onClick={load}>Try again</button></div>;
  if (!data) return <div className="sub-panel"><p className="muted">Loading payments…</p></div>;
  const s = data.summary;

  return (
    <div className="task-stack" data-testid="payments-tab">
      <section className="sub-panel" aria-label="Payment summary">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 14 }}>
          <div><p className="field-label" style={{ margin: 0 }}>Total paid</p><strong style={{ fontSize: 22 }}>{money(s.total_paid_cents)}</strong></div>
          <div><p className="field-label" style={{ margin: 0 }}>Balance owed</p>
            <strong style={{ fontSize: 22, color: s.balance_owed_cents > 0 ? "var(--ne-warning-text)" : undefined }}>{money(s.balance_owed_cents)}</strong></div>
          <div><p className="field-label" style={{ margin: 0 }}>Plan</p>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <strong>{s.plan?.label || "No plan"}</strong><PlanStatusBadge status={s.plan_status} />
            </div>
            {s.plan && s.plan.type !== "free" && s.plan.type !== "paid_in_full" && (
              <span className="muted small">Paid {money(s.total_paid_cents)} of {money(s.expected_to_date_cents)} expected so far</span>
            )}
          </div>
          <div><p className="field-label" style={{ margin: 0 }}>Next due</p><strong>{day(s.next_due_date)}</strong></div>
        </div>
        {canEdit && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
            <button type="button" className="primary-button" onClick={() => { setNotice(null); load(); setRecordOpen(true); }} data-testid="record-payment">Record payment</button>
            <button type="button" className="btn-secondary" onClick={() => { setNotice(null); load(); setTakeOpen(true); }} data-testid="take-payment">Take payment</button>
            <button type="button" className="btn-secondary" onClick={() => setPlanOpen(true)}>{s.plan ? "Change plan" : "Set payment plan"}</button>
          </div>
        )}
        {notice && <p className="small" role="status" style={{ margin: "10px 0 0", color: "var(--ne-success-text)" }}>{notice}</p>}
        {err && <p className="error-text" role="alert" style={{ margin: "10px 0 0" }}>{err}</p>}
      </section>

      <section className="sub-panel" aria-labelledby="payment-history-title">
        <h3 id="payment-history-title" style={{ margin: "0 0 8px" }}>Payment history</h3>
        {data.payments.length === 0 ? (
          <p className="muted small" style={{ margin: 0 }}>No payments yet. Online card and Fanbasis payments show here automatically; use Record payment for cash, Zelle, Cash App, checks or anything paid before the Hub.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="data-table" style={{ width: "100%", minWidth: 760 }}>
              <thead><tr>
                <th>Date paid</th><th>Amount</th><th>Method</th><th>Applied to</th><th>Reference #</th><th>Source</th><th>Recorded by</th><th>Receipt</th><th aria-label="Actions" />
              </tr></thead>
              <tbody>
                {data.payments.map((p) => {
                  const notReceived = !p.voided_at ? NOT_RECEIVED[p.status] : undefined;
                  const dim = !!p.voided_at || !!notReceived;
                  return (
                  <tr key={p.id} style={dim ? { opacity: 0.6 } : undefined}>
                    <td>{paidDay(p)}</td>
                    <td style={dim ? { textDecoration: "line-through" } : undefined}>{money(p.amount_cents)}</td>
                    <td>{PAYMENT_METHOD_LABEL[p.method] || p.method}</td>
                    <td>{appliedLabel(p)}
                      {notReceived && <div className="small" style={{ color: "var(--ne-warning-text)", fontWeight: 600 }}>{notReceived}</div>}
                      {p.voided_at && <div className="small" style={{ color: "var(--ne-red-dark)" }}>Voided {day(p.voided_at)}{p.void_reason ? ` — ${p.void_reason}` : ""}</div>}
                      {p.note && !p.voided_at && <div className="muted small">{p.note}</div>}</td>
                    <td>{p.reference || "—"}</td>
                    <td>{SOURCE_LABEL[p.source] || p.source}</td>
                    <td>{p.recorded_by_name || (p.source === "online" ? "Client" : "—")}</td>
                    <td>{p.receipt_url && !notReceived && !p.voided_at ? <a href={p.receipt_url} target="_blank" rel="noopener noreferrer">View</a> : "—"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button type="button" className="btn-ghost" style={{ height: 26, padding: "0 8px", fontSize: 12 }} onClick={() => showHistory(p)}>History</button>
                      {canEdit && !p.voided_at && !notReceived && (
                        <button type="button" className="btn-ghost" style={{ height: 26, padding: "0 8px", fontSize: 12, color: "var(--ne-red-dark)" }}
                          onClick={() => { setVoiding(p); setVoidReason(""); }} aria-label={`Void ${money(p.amount_cents)} payment from ${paidDay(p)}`}>Void</button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {recordOpen && (
        <RecordPaymentModal clientId={clientId} overview={data} onClose={() => setRecordOpen(false)}
          onSaved={(m) => { setRecordOpen(false); setNotice(m); load(); }} />
      )}
      {takeOpen && (
        <TakePaymentModal clientId={clientId} overview={data} onRefresh={load} onClose={() => setTakeOpen(false)}
          onSaved={(m) => { setTakeOpen(false); setNotice(m); load(); }} />
      )}
      {planOpen && <PlanModal clientId={clientId} current={s.plan} onClose={() => setPlanOpen(false)} onSaved={() => { setPlanOpen(false); setNotice("Payment plan saved."); load(); }} />}

      {voiding && (
        <Modal open title="Void payment" onClose={() => setVoiding(null)}
          footer={<>
            <button type="button" className="btn-secondary" onClick={() => setVoiding(null)} disabled={voidBusy}>Keep payment</button>
            <button type="button" className="primary-button" onClick={doVoid} disabled={voidBusy || voidReason.trim().length < 3}>{voidBusy ? "Voiding…" : "Void payment"}</button>
          </>}>
          <p style={{ marginTop: 0 }}>Void the {money(voiding.amount_cents)} payment from {paidDay(voiding)}? It stays in the history marked as voided — payments are never deleted.</p>
          <label className="form-field"><span>Reason (required)</span>
            <input value={voidReason} onChange={(e) => setVoidReason(e.target.value)} maxLength={300} placeholder="Entered twice, wrong client, bounced check…" /></label>
        </Modal>
      )}
      {history && (
        <Modal open title="Payment change history" onClose={() => setHistory(null)}>
          {history.rows.length === 0 ? <p className="muted">No changes recorded.</p> : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
              {history.rows.map((r, i) => (
                <li key={i} className="small"><strong style={{ textTransform: "capitalize" }}>{r.action === "add" ? "Added" : r.action === "edit" ? "Edited" : "Voided"}</strong> by {r.actor_name || "system"} on {new Date(r.at).toLocaleString()}{r.reason ? ` — ${r.reason}` : ""}</li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </div>
  );
}

function PlanModal({ clientId, current, onClose, onSaved }: {
  clientId: string;
  current: PaymentsOverview["summary"]["plan"];
  onClose: () => void; onSaved: () => void;
}) {
  const [type, setType] = useState<PlanType | "">(current?.type || "");
  const [amount, setAmount] = useState(current?.amount_cents != null ? (current.amount_cents / 100).toFixed(2) : "");
  const [installments, setInstallments] = useState(current?.installments != null ? String(current.installments) : "");
  const [start, setStart] = useState(current?.start_date?.slice(0, 10) || new Date().toLocaleDateString("en-CA"));
  const [note, setNote] = useState(current?.note || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setErr(null);
    const cents = Math.round(Number(amount.replace(/[$,\s]/g, "")) * 100);
    if (type === "custom" && (!Number.isFinite(cents) || cents <= 0)) return setErr("Enter the amount for each payment.");
    if (type === "custom" && installments && (!/^\d+$/.test(installments) || Number(installments) < 1)) return setErr("Enter how many payments, or leave it blank for ongoing.");
    setBusy(true);
    try {
      await paymentsApi.setPlan(clientId, {
        payment_plan: type || null,
        plan_amount_cents: type === "custom" ? cents : null,
        plan_installments: type === "custom" && installments ? Number(installments) : null,
        plan_start_date: type && type !== "free" ? start : null,
        plan_note: note.trim() || null,
      });
      onSaved();
    } catch (e: any) { setErr(e?.body?.message || e?.message || "Couldn't save the plan."); setBusy(false); }
  }

  return (
    <Modal open title="Payment plan" onClose={onClose}
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="primary-button" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save plan"}</button>
      </>}>
      <div className="stacked-form">
        <label className="form-field"><span>Plan</span>
          <select value={type} onChange={(e) => setType(e.target.value as PlanType | "")}>
            {PLAN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select></label>
        {type === "custom" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <label className="form-field"><span>Amount per month</span><input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" /></label>
            <label className="form-field"><span>Number of payments</span><input inputMode="numeric" value={installments} onChange={(e) => setInstallments(e.target.value)} placeholder="Blank = ongoing" /></label>
          </div>
        )}
        {type && type !== "free" && (
          <label className="form-field"><span>Plan start date</span><input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></label>
        )}
        <label className="form-field"><span>Note (optional)</span><input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} /></label>
        <p className="muted small" style={{ margin: 0 }}>A plan only tracks what's expected. Nothing is charged automatically, and invoices are still created only for completed work.</p>
        {err && <p className="error-text" role="alert" style={{ margin: 0 }}>{err}</p>}
      </div>
    </Modal>
  );
}
